<template>
  <div class="md:container md:mx-auto">
    <Head title="Edit Project" />
    <h1 class="text-3xl font-bold">
      <Link class="text-blue-400 hover:text-blue-600" href="/projects">Projects</Link>
      <span class="text-blue-400 font-medium">/</span>
      Edit Project
    </h1>
    <trashed-message v-if="project.deleted_at" class="mb-6" @restore="restore"> This project has been deleted. </trashed-message>

    <!-- Form Container -->
    <form @submit.prevent="update">
      <!-- Main Column Container -->
      <div class="md:flex md:space-x-4">
        <!-- Project Column -->
        <div class="w-full md:w-1/3 p-1">
          <h2 class="pb-4 italic text-xl">Project</h2>
          <div class="flex flex-wrap mb-2 w-full">
            <text-input v-model="form.title" name="title" :error="form.errors.title" class="pb-4 pr-4 w-full" label="Project Title *" placeholder="Project Title" tabindex="1" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.address" name="address" :error="form.errors.address" class="pb-4 pr-4 w-full" label="Project Address" placeholder="Address" tabindex="2" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.city" name="city" :error="form.errors.city" class="pb-4 pr-4 w-full" label="City" placeholder="City" tabindex="3" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.state" name="state" :error="form.errors.state" class="pb-4 pr-4 w-full" label="State/Region" placeholder="State/Region" tabindex="4" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.country" name="country" :error="form.errors.country" class="pb-4 pr-4 w-full" label="Country" placeholder="Select Country" tabindex="5" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.zipcode" name="zipcode" :error="form.errors.zipcode" class="pb-4 pr-4 w-full" label="Zip/Post Code" placeholder="Zip/Post Code" tabindex="6" />
          </div>
          <div v-if="auth.user.is_admin" class="flex flex-wrap mb-2">
            <select-input v-model="form.price_list_id" :error="form.errors.price_list_id" class="pb-4 pr-4 w-full" label="Price List" tabindex="7">
              <option v-for="pl in pricelists" :key="pl.id" :value="pl.id"> {{ pl.name }}</option>
            </select-input>
          </div>
        </div>

        <!-- Customer Column -->
        <div class="w-full md:w-1/3 p-1">
          <h2 class="pb-4 italic text-xl">Customer</h2>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.company" name="company" :error="form.errors.company" class="pb-4 pr-4 w-full" label="Company *" placeholder="Company name" tabindex="8" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.contact" name="contact" :error="form.errors.contact" class="pb-4 pr-4 w-full" label="Contact *" placeholder="Contact name" tabindex="9" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.email" name="email" :error="form.errors.email" class="pb-4 pr-4 w-full" type="email" label="Email *" placeholder="Email Address" tabindex="10" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.phone" name="phone" :error="form.errors.phone" class="pb-4 pr-4 w-full" type="tel" mask="(###) ###-####" label="Phone" placeholder="Phone number" tabindex="11" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.branch_location" name="branch_location" :error="form.errors.branch_location" class="pb-4 pr-4 w-full" label="Branch Location *" placeholder="Branch Location" tabindex="12" required />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.company_city" name="company_city" :error="form.errors.company_city" class="pb-4 pr-4 w-full" label="City" placeholder="Company City" tabindex="13" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.company_state" name="company_state" :error="form.errors.company_state" class="pb-4 pr-4 w-full" label="State" placeholder="Company State" tabindex="14" />
          </div>
        </div>

        <!-- Manufacturer Rep Column -->
        <div class="w-full md:w-1/3 p-1">
          <h2 class="pb-4 italic text-xl">Manufacturer Rep</h2>
          <div class="flex flex-wrap mb-2">
            <label for="dealer" class="font-bold mb-2 w-full">Select/Update Rep Contact</label>
            <v-multiselect
              id="dealer"
              v-model="form.dealer"
              placeholder="Rep Contact"
              track-by="id"
              label="Dealer Contact"
              class="pb-4 pr-4 w-full"
              :options="dealers"
              :loading="isLoading"
              :internal-search="false"
              :custom-label="nameWithMail"
              @select="onUserSelect"
              @search-change="onSearchChange"
            />
          </div>
          <div class="flex flex-wrap justify-end mb-2">
            <button type="button" class="border border-blue-400 py-2 px-8 mb-4 mr-3.5" @click="toggleEditRep">Add New Rep</button>
          </div>
          <h2 class="italic mb-2 mt-24 text-xl">Rep Contact Information</h2>
          <text-input v-model="form.rep_id" name="rep_id" class="hidden" />
          <div class="flex flex-wrap mb-2">
            <select-input :key="selectKey" ref="repAgencySelect" v-model="form.rep_agency" :error="form.errors.rep_agency" :class="{ 'opacity-70': !isEditingRep } " class="pb-4 pr-4 w-full" label="Rep Agency" tabindex="16" :disabled="!isEditingRep && !hasRepErrors">
              <option v-for="agency in agencies" :key="agency.id" :value="agency.id">{{ agency.name }}</option>
            </select-input>
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.rep_first_name" name="rep_first_name" :error="form.errors.rep_first_name" :class="{ 'opacity-70': !isEditingRep }" class="pb-4 pr-2 w-1/2" label="First Name" placeholder="First Name" tabindex="17" :readonly="!isEditingRep && !hasRepErrors" />
            <text-input v-model="form.rep_last_name" name="rep_last_name" :error="form.errors.rep_last_name" :class="{ 'opacity-70': !isEditingRep }" class="pb-4 pl-2 pr-3.5 w-1/2" label="Last Name" placeholder="Last Name" tabindex="18" :readonly="!isEditingRep && !hasRepErrors" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.rep_email" name="rep_email" :error="form.errors.rep_email" :class="{ 'opacity-70': !isEditingRep }" class="pb-4 pr-4 w-full" type="email" label="Email" placeholder="Rep Email" tabindex="19" :readonly="!isEditingRep && !hasRepErrors" />
          </div>
          <div class="flex flex-wrap mb-2">
            <text-input v-model="form.rep_phone" name="rep_phone" :error="form.errors.rep_phone" :class="{ 'opacity-70': !isEditingRep }" class="pb-4 pr-4 w-full" type="tel" label="Phone" mask="(###) ###-####" placeholder="Rep Phone Number" tabindex="20" :readonly="!isEditingRep && !hasRepErrors" />
          </div>
        </div>
      </div>

      <!-- Button Container -->
      <div class="flex items-center px-8 pl-0 py-4 bg-gray-50 border-t border-gray-100 mt-4">
        <button v-if="! project.deleted_at" class="text-red-600 hover:underline ml-4" type="button" @click="destroy">Delete Project</button>
        <loading-button :loading="form.processing" class="btn-blue ml-auto" tabindex="-1" type="submit">Update Project</loading-button>
      </div>
    </form>
  </div>
</template>



<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import VMultiselect from 'vue-multiselect'
import { debounce } from 'lodash'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    LoadingButton,
    TrashedMessage,
    VMultiselect,
  },
  layout: Layout,
  props: {
    auth: Object,
    project: Object,
    pricelists: Array,
    agencies: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        title          : this.project.title,
        address        : this.project.address,
        city           : this.project.city,
        state          : this.project.state,
        country        : this.project.country,
        zipcode        : this.project.zipcode,
        price_list_id  : this.project.price_list_id,
        company        : this.project.company,
        contact        : this.project.contact,
        email          : this.project.email,
        phone          : this.project.phone,
        branch_location: this.project.branch_location,
        company_city   : this.project.company_city,
        company_state  : this.project.company_state,
        dealer         : this.project.dealer,
        dealer_name    : this.project.dealer_name,
        rep_agency     : this.project.rep_agency,
        rep_first_name : this.project.rep_first_name,
        rep_last_name  : this.project.rep_last_name,
        rep_email      : this.project.rep_email,
        rep_phone      : this.project.rep_phone,
        rep_id         : this.project.rep_id,
      }),
      dealers: [],
      isLoading: false,
      selectKey: 0,
      isEditingRep: false,
    }
  },
  computed: {
    hasRepErrors() {
      // Only return true if any of the specific rep fields have errors
      return (
        this.form.errors.rep_agency ||
        this.form.errors.rep_first_name ||
        this.form.errors.rep_last_name ||
        this.form.errors.rep_email ||
        this.form.errors.rep_phone
      );
    },
  },
  watch: {
    // Watch for changes in specific rep errors to enable editing only if they have errors
    hasRepErrors(newVal) {
      if (newVal) {
        this.isEditingRep = true; // Enable editing mode if specific rep fields have errors
      }
    },
  },
  methods: {

    update() {
      this.form.put(`/projects/${this.project.id}`)
    },
    destroy() {
      if (confirm('Are you sure you want to delete this project?')) {
        this.$inertia.delete(`/projects/${this.project.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this project?')) {
        this.$inertia.put(`/projects/${this.project.id}/restore`)
      }
    },
    onSearchChange: debounce(function(query) {
      this.getData(query);
    }, 500),
    getCsrfToken() {
      const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    },
    getData(query) {
      this.isLoading = true;
      if (query.length > 0) {
        fetch(`/search-users?userSearch=${query}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': this.getCsrfToken(), // Usar el token CSRF
          },
          credentials: 'same-origin' // Enviar cookies de sesión para autenticación
        })
        .then(response => response.json())
        .then(data => {
          this.dealers = data.users || [];
          this.isLoading = false;
        })
        .catch(error => {
          this.isLoading = false;
        });
      } else {
        this.dealers = [];
        this.isLoading = false;
      }
    },
    nameWithMail ({ name }) {
      return `${name}`;
    },
    onUserSelect(selectedOption) {
      if (selectedOption) {
        this.form.rep_first_name = selectedOption.first_name;
        this.form.rep_last_name = selectedOption.last_name;
        this.form.rep_email = selectedOption.email;
        this.form.rep_phone = selectedOption.phone;

        // Set rep_agency and increment selectKey to force re-render
        this.form.rep_agency = selectedOption.company_id;
        this.selectKey += 1; // Update the key to trigger reactivity
        this.form.dealer = null;
        this.form.rep_id = selectedOption.id;
      }
    },
    toggleEditRep() {
      this.form.rep_first_name = null;
      this.form.rep_last_name = null;
      this.form.rep_email = null;
      this.form.rep_phone = null;

      // Set rep_agency and increment selectKey to force re-render
      this.form.rep_agency = null;
      this.selectKey += 1; // Update the key to trigger reactivity

      this.form.dealer = null;
      this.isEditingRep = true; // Enable editing mode
      this.form.rep_id = 'new';
    },
  },
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style>
@media (max-width: 1024px) {
    .multiselect {
        max-width: 22.5rem !important;
    }
}
.multiselect__select {
  right: 20px !important;
}

.multiselect__option--highlight,
.multiselect__option--highlight::after {
  background-color: #0052A4 !important;
}
.multiselect__spinner {
  border-color: #0052A4 transparent transparent transparent;
}
</style>
